import React, { useState, useEffect } from 'react';
import { File, Download, FileText, Image, Video, Archive, FileIcon } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../../../../../config/firebase';
import { logError, logInfo } from '../../../../../../../utils/logger';

const FilesTab = ({ surveyId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchFiles = async () => {
      if (!surveyId) return;
      
      setLoading(true);
      try {
        // Query files collection for this survey
        const filesRef = collection(db, 'files');
        const q = query(filesRef, where('surveyId', '==', surveyId));
        const snapshot = await getDocs(q);
        
        const filesList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setFiles(filesList);
        logInfo(`Loaded ${filesList.length} files for survey ${surveyId}`);
      } catch (error) {
        logError('Failed to load files:', error);
        setError('Failed to load files. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [surveyId]);

  const filteredFiles = files.filter(file => {
    if (filter === 'all') return true;
    return file.type === filter;
  });

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image':
        return <Image className="w-5 h-5 text-blue-400" />;
      case 'document':
        return <FileText className="w-5 h-5 text-green-400" />;
      case 'video':
        return <Video className="w-5 h-5 text-red-400" />;
      case 'archive':
        return <Archive className="w-5 h-5 text-yellow-400" />;
      default:
        return <FileIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-medium">Survey Files</h3>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-gray-700 border-gray-600 text-white text-sm rounded-lg p-1"
        >
          <option value="all">All Files</option>
          <option value="image">Images</option>
          <option value="document">Documents</option>
          <option value="video">Videos</option>
          <option value="archive">Archives</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
        </div>
      ) : error ? (
        <div className="bg-red-500/10 text-red-400 p-3 rounded-lg text-sm">
          {error}
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="text-center py-4 text-gray-400">
          {files.length === 0 ? 'No files available for this survey' : 'No files match the selected filter'}
        </div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
          {filteredFiles.map((file) => (
            <div 
              key={file.id}
              className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                {getFileIcon(file.type)}
                <div className="truncate">
                  <div className="text-white text-sm truncate">{file.name}</div>
                  <div className="text-gray-400 text-xs">
                    {new Date(file.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <a 
                href={file.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilesTab;